import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {
  CreateFullReport,
  GetReportById,
  GetReports,
  GetCarRecalls,
  GetVehicleDataByReportResult,
  GetStatsByFilter,
  GetDealerByFilter,
  GetResultById,
  ObserveReportById,
  GetPublicReports,
  GetAlreadyCreatedReports,
  GetNpiDataByReport,
  GetFavoriteReportsId,
  SetFavoriteReport,
  RefreshReport,
  CreateQuickReport,
  GetSimulationResultsById,
  ObserveSimulationResultsById,
  GetSimulationDataPointsById,
  ObserveSimulationDataPointsById,
  UpdateQuickReport,
  CheckInventoryExistence,
  GetGasPrice,
  GetPossibleReportsInventory,
  GetElectricityPrice,
  GetFuelEconomyData,
  GetListingFilters,
  GetVehicleDataById
} from './report.actions';
import {
  DealerResult,
  InventorySearchResult,
  MarketStatsResult,
  RawReportData,
  RecallsResult,
  ReportData,
  QuickReportDataPoints,
  ReportResult,
  ResultWrapper,
  QuickReportResult,
  QuickReportResultWrapper,
  CarCombination,
  QuickReportDataPointObject,
  ServerResponse,
  ActiveStatsData,
  ActiveFacetsData,
  FuelEconomyData,
  ListingFilters,
  InventoryExtras, ListingResult
} from '@shared/interfaces';
import {ReportService} from '@shared/service/report/report.service';
import {StorageService} from '@app/service/util/storage.service';
import {
  EXISTING_REPORT_PRICE_KEY, EXISTING_REPORT_SEND_OPTIONS_KEY,
  EXISTING_REPORT_VEHICLE_KEY,
  UNSAVED_PRICE_KEY,
  UNSAVED_SEND_OPTIONS_KEY,
  UNSAVED_VEHICLE_KEY
} from '@shared/helpers/variables';
import {AnalyticsService} from '@app/service/util/analytics.service';
import {checkNested, getNested, serverResponseToast} from '@shared/helpers/helpers';
import {AuthState} from '@shared/state/auth/auth.state';
import {ToastService} from '@app/service/util/toast.service';
import {RoutingService} from '@app/service/routing/routing.service';
import {Injectable, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs';
import moment from 'moment';
import {LoggerService} from '@app/service/logger/logger.service';
import {patch} from '@ngxs/store/operators';
import {DocumentSnapshot} from '@firebase/firestore';

export interface ReportStateModel {
  reports: { [k: string]: ReportData };
  hasInventory: { [k: string]: boolean };
  reportsCreatedList: { [k: string]: boolean };
  hasPublicReports: boolean;
  unsavedReport: RawReportData;
  npiData: { [k: string]: any };
  reportResults: { [k: string]: ReportResult };
  reportSimulationResults: { [k: string]: QuickReportResultWrapper }; // key is the {reportId}_{simulationId}
  reportSimulationDataPoints: { [k: string]: QuickReportDataPointObject }; // key is the {reportId}_{simulationId}
  carRecalls: { [k: string]: RecallsResult };
  activeInventoryByZip: {
    [k: string]: {
      listings: { [l: string]: ListingResult };
      numFound: number;
      totalNumFound: number;
      savedKeys: boolean;
    }
  };
  activeInventoryByState: { [k: string]: InventorySearchResult };
  privateInventoryByZip: { [k: string]: InventorySearchResult };
  privateInventoryByState: { [k: string]: InventorySearchResult };
  recentInventoryByZip: { [k: string]: InventorySearchResult };
  activeStatsByZip: { [k: string]: ActiveStatsData };
  activeStatsByState: { [k: string]: ActiveStatsData };
  activeStatsByCountry: { [k: string]: ActiveStatsData };
  activeFacetsByZip: { [k: string]: ActiveFacetsData };
  activeFacetsByState: { [k: string]: ActiveFacetsData };
  activeFacetsByCountry: { [k: string]: ActiveFacetsData };
  activeRangeFacetsByZip: { [k: string]: ActiveFacetsData };
  activeRangeFacetsByState: { [k: string]: ActiveFacetsData };
  activeRangeFacetsByCountry: { [k: string]: ActiveFacetsData };
  mdsStatsByCountry: { [k: string]: MarketStatsResult };
  mdsStatsByState: { [k: string]: MarketStatsResult };
  mdsStatsByZip: { [k: string]: MarketStatsResult };
  soldStatsByZip: { [k: string]: MarketStatsResult };
  soldStatsByState: { [k: string]: MarketStatsResult };
  dealersByZip: { [k: string]: DealerResult[] };
  dealersByState: { [k: string]: DealerResult[] };
  fuelEconomyData: { [k: string]: FuelEconomyData[] };
  favoriteReportsId: { [k: string]: boolean };
  gasPrices: { [k: string]: number };
  electricityPrices: { [k: string]: number };
  possibleReportsInventory: { [k: string]: InventorySearchResult };
  listingFilters: ListingFilters;
  inventoryExtras: { [k: string]: InventoryExtras };
}

@State<ReportStateModel>({
  name: 'report',
  defaults: {
    reports: {},
    hasInventory: {},
    reportsCreatedList: {},
    hasPublicReports: false,
    unsavedReport: null,
    npiData: {},
    reportResults: {},
    reportSimulationResults: {},
    reportSimulationDataPoints: {},
    carRecalls: {},
    activeInventoryByZip: {},
    activeInventoryByState: {},
    privateInventoryByZip: {},
    privateInventoryByState: {},
    recentInventoryByZip: {},
    activeStatsByZip: {},
    activeStatsByState: {},
    activeStatsByCountry: {},
    activeFacetsByZip: {},
    activeFacetsByState: {},
    activeFacetsByCountry: {},
    activeRangeFacetsByZip: {},
    activeRangeFacetsByState: {},
    activeRangeFacetsByCountry: {},
    mdsStatsByCountry: {},
    mdsStatsByState: {},
    mdsStatsByZip: {},
    soldStatsByZip: {},
    soldStatsByState: {},
    dealersByZip: {},
    dealersByState: {},
    fuelEconomyData: {},
    favoriteReportsId: {},
    gasPrices: {},
    electricityPrices: {},
    possibleReportsInventory: {},
    listingFilters: {},
    inventoryExtras: {}
  }
})
@Injectable()
export class ReportState implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  hasPulledAllUserReports = false;
  hasPulledAllPublicReports = false;

  createdReportsDateKey = `${moment().format('M')}_${moment().format('YYYY')}`;

  constructor(private reportService: ReportService, private storage: StorageService, private toast: ToastService,
              private analytics: AnalyticsService, private routingService: RoutingService, private store: Store,
              private logger: LoggerService) {
  }

  @Selector()
  static getReports(state: ReportStateModel) {
    return state.reports;
  }

  @Selector()
  static getReportsCreatedList(state: ReportStateModel) {
    return state.reportsCreatedList;
  }

  @Selector()
  static getUnsavedReport(state: ReportStateModel) {
    return state.unsavedReport;
  }

  @Selector()
  static getHasPublicReports(state: ReportStateModel) {
    return state.hasPublicReports;
  }

  @Selector()
  static getNpiData(state: ReportStateModel) {
    return state.npiData;
  }

  @Selector()
  static getReportResults(state: ReportStateModel) {
    return state.reportResults;
  }

  @Selector()
  static getReportSimulationResults(state: ReportStateModel) {
    return state.reportSimulationResults;
  }

  @Selector()
  static getReportSimulationDataPoints(state: ReportStateModel) {
    return state.reportSimulationDataPoints;
  }

  @Selector()
  static getCarRecalls(state: ReportStateModel) {
    return state.carRecalls;
  }

  @Selector()
  static getActiveInventoryByZip(state: ReportStateModel) {
    return state.activeInventoryByZip;
  }

  @Selector()
  static getActiveInventoryByState(state: ReportStateModel) {
    return state.activeInventoryByState;
  }

  @Selector()
  static getActiveFacetsByZip(state: ReportStateModel) {
    return state.activeFacetsByZip;
  }

  @Selector()
  static getInventoryExtras(state: ReportStateModel) {
    return state.inventoryExtras;
  }

  @Selector()
  static getActiveFacetsByState(state: ReportStateModel) {
    return state.activeFacetsByState;
  }

  @Selector()
  static getActiveRangeFacetsByZip(state: ReportStateModel) {
    return state.activeRangeFacetsByZip;
  }

  @Selector()
  static getActiveRangeFacetsByState(state: ReportStateModel) {
    return state.activeRangeFacetsByState;
  }

  @Selector()
  static getPrivateInventoryByZip(state: ReportStateModel) {
    return state.privateInventoryByZip;
  }

  @Selector()
  static getPrivateInventoryByState(state: ReportStateModel) {
    return state.privateInventoryByState;
  }

  @Selector()
  static getRecentInventoryByZip(state: ReportStateModel) {
    return state.recentInventoryByZip;
  }

  @Selector()
  static getActiveStatsByZip(state: ReportStateModel) {
    return state.activeStatsByZip;
  }

  @Selector()
  static getActiveStatsByState(state: ReportStateModel) {
    return state.activeStatsByState;
  }

  @Selector()
  static getActiveStatsByCountry(state: ReportStateModel) {
    return state.activeStatsByCountry;
  }

  @Selector()
  static getMdsStatsByState(state: ReportStateModel) {
    return state.mdsStatsByState;
  }

  @Selector()
  static getMdsStatsByZip(state: ReportStateModel) {
    return state.mdsStatsByZip;
  }

  @Selector()
  static getSoldStatsByZip(state: ReportStateModel) {
    return state.soldStatsByZip;
  }

  @Selector()
  static getSoldStatsByState(state: ReportStateModel) {
    return state.soldStatsByState;
  }

  @Selector()
  static getDealersByZip(state: ReportStateModel) {
    return state.dealersByZip;
  }

  @Selector()
  static getDealersByState(state: ReportStateModel) {
    return state.dealersByState;
  }

  @Selector()
  static getFuelEconomyData(state: ReportStateModel) {
    return state.fuelEconomyData;
  }

  @Selector()
  static getFavoriteReportsId(state: ReportStateModel) {
    return state.favoriteReportsId;
  }

  @Selector()
  static checkHasInventory(state: ReportStateModel) {
    return state.hasInventory;
  }

  @Selector()
  static getGasPrices(state: ReportStateModel) {
    return state.gasPrices;
  }

  @Selector()
  static getElectricityPrices(state: ReportStateModel) {
    return state.electricityPrices;
  }

  @Selector()
  static getPossibleReportsInventory(state: ReportStateModel) {
    return state.possibleReportsInventory;
  }

  @Selector()
  static getListingFilters(state: ReportStateModel) {
    return state.listingFilters;
  }

  @Action(GetReports)
  getReportsData(sc: StateContext<ReportStateModel>) {
    if (this.hasPulledAllUserReports) {
      return Promise.resolve(true);
    }

    try {
      // As we get more users, we should think of implementing pagination
      return this.reportService.getUserReports()
        .then((result: ReportData[]) => {
          if (result && result.length > 0) {
            this.hasPulledAllUserReports = true;
          }
          const newReports = {};
          for (const report of result) {
            newReports[report.id] = report;
          }
          sc.patchState({
            reports: {
              ...sc.getState().reports,
              ...newReports
            }
          });
        }, (error) => {
          this.logger.error(`GetReports`, JSON.stringify(error.stack));
        });
    } catch (e) {
      this.logger.error(`GetReports`, JSON.stringify(e.stack));
    }
  }

  @Action(GetPublicReports)
  getPublicReportsData(sc: StateContext<ReportStateModel>) {
    if (this.hasPulledAllPublicReports) {
      return Promise.resolve(true);
    }

    try {
      // As we get more users, we should think of implementing pagination
      return this.reportService.getPublicReports()
        .then((result: ReportData[] | ServerResponse) => {
          let reportsData;
          if ('data' in result) {
            reportsData = result.data.reportsData || [];
          } else {
            reportsData = result as ReportData[];
          }
          if (reportsData && reportsData.length > 0) {
            this.hasPulledAllPublicReports = true;
            sc.patchState({
              hasPublicReports: true
            });
          }
          const newReports = {};
          for (const report of reportsData) {
            newReports[report.id] = report;
          }
          sc.patchState({
            reports: {
              ...sc.getState().reports,
              ...newReports
            }
          });
        });
    } catch (e) {
      this.logger.error(`GetPublicReports`, JSON.stringify(e.stack));
    }
  }

  @Action(GetReportById)
  getReportById(sc: StateContext<ReportStateModel>, {payload}: GetReportById) {
    // As we get more users, we should think of implementing pagination
    const reportId = payload.reportId;
    const allReports = sc.getState().reports || {};

    if (allReports && allReports[reportId]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getReportById(reportId)
        .then((result: ReportData | ServerResponse) => {
          let reportData;
          if (getNested(result, 'data')) {
            reportData = (result as ServerResponse).data.reportData || {};
          } else {
            reportData = result as ReportData;
          }
          if (!reportData) {
            return;
          }
          sc.patchState({
            reports: Object.assign({}, sc.getState().reports, {[reportId]: reportData})
          });
        });
    } catch (e) {
      this.logger.error(`GetReportById`, JSON.stringify(e.stack));
    }
  }

  @Action(GetNpiDataByReport)
  getNpiEmailsByReport(sc: StateContext<ReportStateModel>, {payload}: GetNpiDataByReport) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const npiDoc = payload.npiDoc;
    const allNpiData = Object.assign({}, sc.getState().npiData);

    if (checkNested(allNpiData, `${reportId}_${resultId}`, npiDoc)) {
      return Promise.resolve(true);
    }

    try {
      return this.reportService.getNpiData(reportId, npiDoc)
        .then((result: any) => {
          let _npiData = {};
          if (allNpiData[`${reportId}_${resultId}`]) {
            _npiData = allNpiData[`${reportId}_${resultId}`];
          }
          _npiData = {
            ..._npiData,
            [npiDoc]: result
          };
          sc.patchState({
            npiData: Object.assign({}, sc.getState().npiData, {[`${reportId}_${resultId}`]: _npiData})
          });
        });
    } catch (e) {
      this.logger.error(`GetNpiDataByReport`, JSON.stringify(e.stack));
    }
  }

  @Action(GetAlreadyCreatedReports)
  getAlreadyCreatedReports(sc: StateContext<ReportStateModel>) {
    try {
      return this.reportService.getReportCreatedList(this.createdReportsDateKey)
        .then(async (reportsCreatedList: any) => {
          this.analytics.trackAPICallsMetric('report', 'reportCreatedList', 'get', 'success');

          // Join with the created reports found locally in case user is trying to be slick
          const retrievedLocally = this.storage.retrieveValue(this.createdReportsDateKey) || {};
          reportsCreatedList = Object.assign({}, reportsCreatedList, retrievedLocally);
          sc.patchState({
            reportsCreatedList
          });
        });
    } catch (e) {
      this.logger.error(`GetAlreadyCreatedReports`, JSON.stringify(e.stack));
    }
  }

  @Action(GetResultById)
  getResultById(sc: StateContext<ReportStateModel>, {payload}: GetResultById) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const allReportResults = sc.getState().reportResults;

    if (allReportResults && allReportResults[`${reportId}_${resultId}`]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getResultById(reportId, resultId)
        .then((result: ReportResult) => {
          sc.patchState({
            reportResults: Object.assign({}, sc.getState().reportResults, {[`${reportId}_${resultId}`]: result})
          });
        });
    } catch (e) {
      this.logger.error(`GetResultById`, JSON.stringify(e.stack));
    }
  }

  @Action(GetFuelEconomyData)
  getFuelEconomyDataById(sc: StateContext<ReportStateModel>, {payload}: GetFuelEconomyData) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const allFuelEconomyData = sc.getState().fuelEconomyData;

    if (allFuelEconomyData && allFuelEconomyData[`${reportId}_${resultId}`]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getFuelEconomyDataById(reportId, resultId)
        .then((result: { 'fuelEconomyData': FuelEconomyData[] }) => {
          if (!result || !result.fuelEconomyData) {
            return;
          }
          sc.patchState({
            fuelEconomyData: Object.assign({}, sc.getState().fuelEconomyData, {[`${reportId}_${resultId}`]: result.fuelEconomyData})
          });
        });
    } catch (e) {
      this.logger.error(`GetFuelEconomyData`, JSON.stringify(e.stack));
    }
  }

  @Action(GetSimulationResultsById)
  getSimulationResultById(sc: StateContext<ReportStateModel>, {payload}: GetSimulationResultsById) {
    const reportId = payload.reportId;
    const simulationId = payload.simulationId;
    const allReportSimulationResults = sc.getState().reportSimulationResults;
    const simulationSaveKey = `${reportId}_${simulationId}`;

    if (allReportSimulationResults && allReportSimulationResults[`${reportId}_${simulationId}`]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getSimulationResultsById(reportId, simulationId)
        .then((result: { [k: string]: QuickReportResult }) => {
          sc.patchState({
            reportSimulationResults: Object.assign({}, sc.getState().reportSimulationResults, {
              [simulationSaveKey]: this.generateSimulationResult(result)
            })
          });
        });
    } catch (e) {
      this.logger.error(`GetSimulationResultsById`, JSON.stringify(e.stack));
    }
  }

  @Action(ObserveSimulationResultsById)
  observeSimulationResultById(sc: StateContext<ReportStateModel>, {payload}: ObserveSimulationResultsById) {
    const reportId = payload.reportId;
    const simulationId = payload.simulationId;
    const allReportSimulationResults = sc.getState().reportSimulationResults;
    const simulationSaveKey = `${reportId}_${simulationId}`;

    if (allReportSimulationResults && allReportSimulationResults[simulationSaveKey]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.observeSimulationResultsById(reportId, simulationId, {
          next: (docSnapshot: DocumentSnapshot<ReportData>) => {
            const docInfo = this.mapDoc(docSnapshot);
            const result = {
              ...docInfo
            };
            sc.patchState({
              reportSimulationResults: Object.assign({}, sc.getState().reportSimulationResults, {
                [simulationSaveKey]: this.generateSimulationResult(result)
              })
            });
          },
          error: (error: any) => {
            throw new Error(error);
          }
        }
      );
    } catch (e) {
      this.logger.error(`ObserveSimulationResultsById`, JSON.stringify(e.stack));
    }
  }

  generateSimulationResult(result: { [k: string]: QuickReportResult }): QuickReportResultWrapper {
    const transformedResult: { [k: string]: any[] } = {};
    const allTrims = new Set<string>();
    let lowestPrice = Number.MAX_SAFE_INTEGER;
    let highestPrice = Number.MIN_SAFE_INTEGER;
    let count = 0;
    for (const key in result) {
      if (!result.hasOwnProperty(key) || !key.includes('_')) {
        continue;
      }
      const simResult = result[key];
      const trim = simResult.trim;
      const price = simResult.simulated_price;

      if (!transformedResult[trim]) {
        transformedResult[trim] = [];
      }

      transformedResult[trim].push({
        trim,
        exteriorColor: simResult.exterior_color,
        interiorColor: simResult.interior_color,
        simulatedPrice: simResult.simulated_price
      });
      count += 1;
      allTrims.add(simResult.trim);

      if (price < lowestPrice) {
        lowestPrice = price;
      }
      if (price > highestPrice) {
        highestPrice = price;
      }
    }
    // Sort the result
    for (const trim in transformedResult) {
      if (!transformedResult.hasOwnProperty(trim)) {
        continue;
      }

      transformedResult[trim].sort((a: CarCombination, b: CarCombination) => {
        return a.simulatedPrice - b.simulatedPrice;
      });
    }
    return {
      lowestPrice,
      highestPrice,
      simulationCount: count,
      allTrims: Array.from(allTrims).sort(),
      simulatedResults: transformedResult
    };
  }

  @Action(GetSimulationDataPointsById)
  getSimulationDataPointsById(sc: StateContext<ReportStateModel>, {payload}: GetSimulationDataPointsById) {
    const reportId = payload.reportId;
    const simulationId = payload.simulationId;
    const allReportSimulationDataPoints = sc.getState().reportSimulationDataPoints;
    const simulationSaveKey = `${reportId}_${simulationId}`;

    if (allReportSimulationDataPoints && allReportSimulationDataPoints[`${reportId}_${simulationId}`]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getSimulationDataPointsById(reportId, simulationId)
        .then((result: QuickReportDataPoints) => {
          sc.patchState({
            reportSimulationDataPoints: Object.assign({}, sc.getState().reportSimulationDataPoints, {
              [simulationSaveKey]: this.generateSimulationDataPoints(result)
            })
          });
        });
    } catch (e) {
      this.logger.error(`GetSimulationDataPointsById`, JSON.stringify(e.stack));
    }
  }

  @Action(ObserveSimulationDataPointsById)
  observeSimulationDataPointsById(sc: StateContext<ReportStateModel>, {payload}: ObserveSimulationDataPointsById) {
    const reportId = payload.reportId;
    const simulationId = payload.simulationId;
    const allReportSimulationDataPoints = sc.getState().reportSimulationDataPoints;
    const simulationSaveKey = `${reportId}_${simulationId}`;

    if (allReportSimulationDataPoints && allReportSimulationDataPoints[simulationSaveKey]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.observeSimulationDataPointsById(reportId, simulationId, {
          next: (docSnapshot: DocumentSnapshot<ReportData>) => {
            const docInfo = this.mapDoc(docSnapshot);
            const result = {
              ...docInfo
            };

            sc.patchState({
              reportSimulationDataPoints: Object.assign({}, sc.getState().reportSimulationDataPoints, {
                [simulationSaveKey]: this.generateSimulationDataPoints(result)
              })
            });
          },
          error: (error: any) => {
            throw new Error(error);
          }
        }
      );
    } catch (e) {
      this.logger.error(`ObserveSimulationDataPointsById`, JSON.stringify(e.stack));
    }
  }

  generateSimulationDataPoints(result: Partial<QuickReportDataPoints> = {}) {
    const finalResult: QuickReportDataPointObject = {
      totalListingCount: result['trims'].count
    };
    for (const datapoint in result) {
      if (!result.hasOwnProperty(datapoint) || result[datapoint].constructor !== Object) {
        continue;
      }
      finalResult[datapoint] = {
        totalCount: result[datapoint]['keys'].length,
        dataPointList: result[datapoint]['values']
      };
    }
    return finalResult;
  }

  @Action(ObserveReportById)
  observeReportById(sc: StateContext<ReportStateModel>, {payload}: ObserveReportById) {
    const reportId = payload.reportId;
    const action = payload.action;
    const allReports = sc.getState().reports || {};
    const userId = this.store.selectSnapshot(AuthState.getUserID);

    if (allReports && allReports[reportId] && !action) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.observeReportById(reportId, {
          next: (docSnapshot: DocumentSnapshot<ReportData>) => {
            // const source = docSnapshot.metadata.hasPendingWrites ? 'Local' : 'Server';
            const docInfo = this.mapDoc(docSnapshot);
            const newReportData = {
              ...docInfo,
              reportOwner: docInfo?.createdBy === userId ? 'user' : 'public'
            };
            sc.setState(
              patch<ReportStateModel>({
                reports: patch<ReportStateModel['reports']>({
                  [reportId]: newReportData
                })
              })
            );
          },
          error: (error: any) => {
            throw new Error(error);
          }
        }
      );
    } catch (e) {
      this.logger.error(`ObserveReportById`, JSON.stringify(e.stack));
    }
  }

  @Action(GetCarRecalls)
  getRecallsForReport(sc: StateContext<ReportStateModel>, {payload}: GetCarRecalls) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const allCarRecalls = sc.getState().carRecalls;

    if (allCarRecalls && allCarRecalls[`${reportId}_${resultId}`]) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getRecallsForReport(reportId, resultId)
        .then((result: any) => {
          sc.patchState({
            carRecalls: Object.assign({}, sc.getState().carRecalls, {[`${reportId}_${resultId}`]: result.data})
          });
        });
    } catch (e) {
      this.logger.error(`GetCarRecalls`, JSON.stringify(e.stack));
    }
  }

  /**
   * Filter in the payload will always be the state management key, since it is the unique part.
   * Pass the key into the function as the document key. Get the document key from DATABASE_RESULT_KEYS
   * @param sc
   * @param payload
   */
  @Action(GetVehicleDataByReportResult)
  getInventory(sc: StateContext<ReportStateModel>, {payload}: GetVehicleDataByReportResult) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const sectionId = payload.sectionId; // The DATABASE_RESULT_KEYS object key containing docId and stateName
    const allInvSearchResults = sc.getState()[sectionId];

    // Looking for the state[sectionId] does not exist, return undefined
    if (allInvSearchResults === undefined ||
      checkNested(allInvSearchResults, `${reportId}_${resultId}`, 'savedKeys', sectionId)) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getInventoryInfo(reportId, resultId, sectionId)
        .then((result: ListingResult[] | ServerResponse) => {
          let inventoryData = result as ListingResult[];
          if ('data' in result) {
            inventoryData = result.data.inventoryData || {};
          }
          if (!inventoryData) {
            return;
          }
          let allReportInventoryData;

          allReportInventoryData = sc.getState()[sectionId][`${reportId}_${resultId}`] || {};

          allReportInventoryData = {
            ...allReportInventoryData,
            listings: this.mapListingData(allReportInventoryData.listings, inventoryData),
            savedKeys: {
              ...allReportInventoryData.savedKeys,
              [sectionId]: true
            }
          };
          sc.patchState({
            [sectionId]: Object.assign({}, sc.getState()[sectionId], {[`${reportId}_${resultId}`]: allReportInventoryData})
          });
        });
    } catch (e) {
      this.logger.error(`GetVehicleDataByReportResult`, JSON.stringify(e.stack));
    }
  }

  mapListingData(oldListingData: ListingResult[], newListingData: ListingResult[]): { [k: string]: ListingResult } {
    // Combine all listing data making sure to use the new listing data
    const allListingData = {};

    if (oldListingData) {
      for (const id in oldListingData) {
        allListingData[id] = oldListingData[id];
      }
    }

    if (newListingData) {
      for (const listing of newListingData) {
        allListingData[listing.id] = listing;
      }
    }
    return allListingData;
  }

  /**
   * Get the inventory by the reportId and resultId as well as vehicleId
   * @param sc
   * @param payload
   */
  @Action(GetVehicleDataById)
  getInventoryById(sc: StateContext<ReportStateModel>, {payload}: GetVehicleDataById) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const vehicleId = payload.vehicleId;
    const sectionId = 'activeInventoryByZip';
    const allInvSearchResults = sc.getState()[sectionId];

    // Looking for the state[sectionId] does not exist, return undefined
    if (allInvSearchResults === undefined ||
      checkNested(allInvSearchResults, `${reportId}_${resultId}`, 'listings', vehicleId)) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getInventoryInfoById(reportId, resultId, vehicleId)
        .then((result: ListingResult | ServerResponse) => {
          let inventoryData = result;
          if ('data' in result) {
            inventoryData = result.data.inventoryData || {};
          }
          if (!inventoryData) {
            return;
          }
          let allReportInventoryData;

          allReportInventoryData = sc.getState()[sectionId][`${reportId}_${resultId}`] || {};

          allReportInventoryData = {
            ...allReportInventoryData,
            listings: {
              ...(allReportInventoryData.listings || {}),
              [vehicleId]: inventoryData
            }
          };
          sc.patchState({
            [sectionId]: Object.assign({}, sc.getState()[sectionId], {[`${reportId}_${resultId}`]: allReportInventoryData})
          });
        });
    } catch (e) {
      this.logger.error(`GetVehicleDataByReportResult`, JSON.stringify(e.stack));
    }
  }

  /**
   * Filter in the payload will always be the state management key, since it is the unique part.
   * Pass the key into the function as the document key. Get the document key from DATABASE_RESULT_KEYS
   * @param sc
   * @param payload
   */
  @Action(GetStatsByFilter)
  getMarketStats(sc: StateContext<ReportStateModel>, {payload}: GetStatsByFilter) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const sectionId = payload.sectionId;
    const allMarketStatsResults = sc.getState()[sectionId];

    if (allMarketStatsResults === null || (allMarketStatsResults && allMarketStatsResults[`${reportId}_${resultId}`])) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getMarketStats(reportId, resultId, sectionId)
        .then((result: ResultWrapper<MarketStatsResult>) => {
          sc.patchState({
            [sectionId]: Object.assign({}, sc.getState()[sectionId], {[`${reportId}_${resultId}`]: result})
          });
        });
    } catch (e) {
      this.logger.error(`GetStatsByFilter`, JSON.stringify(e.stack));
    }
  }

  /**
   * Filter in the payload will always be the state management key, since it is the unique part.
   * Pass the key into the function as the document key. Get the document key from DATABASE_RESULT_KEYS
   * @param sc
   * @param payload
   */
  @Action(GetDealerByFilter)
  getDealers(sc: StateContext<ReportStateModel>, {payload}: GetDealerByFilter) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const sectionId = payload.sectionId;
    const allDealerInfoResults = sc.getState()[sectionId];

    if (allDealerInfoResults === null || (allDealerInfoResults && allDealerInfoResults[`${reportId}_${resultId}`])) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getDealers(reportId, resultId, sectionId)
        .then((result: ResultWrapper<DealerResult[]>) => {
          sc.patchState({
            [sectionId]: Object.assign({}, sc.getState()[sectionId], {[`${reportId}_${resultId}`]: result})
          });
        });
    } catch (e) {
      this.logger.error(`GetDealerByFilter`, JSON.stringify(e.stack));
    }
  }

  @Action(CreateFullReport)
  createReport(sc: StateContext<ReportStateModel>, {payload}: CreateFullReport) {
    try {
      const reportData = payload.reportData;
      return this.reportService.createFullReport(reportData)
        .then(async (result) => {
          this.storage.clear(UNSAVED_VEHICLE_KEY);
          this.storage.clear(UNSAVED_SEND_OPTIONS_KEY);
          this.storage.clear(UNSAVED_PRICE_KEY);
          const shouldCreateAccount = reportData.sendOptions.createAccount === 'yes';
          const reportId = result.data.reportId;

          if (reportData.paymentIntent.id === 'free') {
            this.storeCreatedReportLocal(reportData);
            this.analytics.trackBusinessMetric('report', 'free', 'create', 'success', {
              vehicle: reportData.vehicle
            });
          } else {
            this.analytics.trackBusinessMetric('report', 'paid', 'create', 'success', {
              priceId: reportData.price.id,
              vehicle: reportData.vehicle
            });
          }
          sc.patchState({
            reports: Object.assign({}, sc.getState().reports, {
              [reportId]: {
                ...result.data.finalReportData,
                reportOwner: 'user',
                id: reportId
              }
            })
          });

          setTimeout(() => {
            serverResponseToast(this.toast, result);

            const isAnon = this.store.selectSnapshot(AuthState.getIsAnonymous);
            if (shouldCreateAccount && isAnon) {
              this.routingService.navigateRegister({
                email: reportData.sendOptions.primaryEmail,
                isCreate: true,
                redirectTo: `r/${reportId}`
              });
              this.toast.info('While you\'re waiting for your report, you can register an account!');
            } else {
              // Not creating an account, redirect to the report page
              this.routingService.navigateToReport(reportId, {isGenerating: 'true'});
            }
          }, 200);
        }, (e) => {
          this.toast.error(e.message);
          throw new Error(e.message);
        });
    } catch (e) {
      this.toast.error(e.message);
      this.logger.error(`CreateFullReport`, JSON.stringify(e.stack));
    }
  }

  @Action(CreateQuickReport)
  createQuickReport(sc: StateContext<ReportStateModel>, {payload}: CreateQuickReport) {
    const simulatedInfoData = payload.simulatedInfoData;
    const additionalReportMetadata = payload.additionalReportMetadata;
    return this.reportService.createQuickReport(simulatedInfoData, additionalReportMetadata)
      .then(async (result) => {
        const reportId = result.data.reportId;
        const simulationId = result.data.simulationId;
        this.routingService.navigateToQuickReport(reportId, {isGenerating: 'true', simulationId});
        serverResponseToast(this.toast, result);

        sc.patchState({
          reports: Object.assign({}, sc.getState().reports, {
            [reportId]: {
              ...result.data.finalReportData,
              reportOwner: 'user',
              id: reportId
            }
          })
        });
        this.analytics.trackBusinessMetric('quick-report', null, 'create', 'success');
      }, (e: ServerResponse) => {
        this.toast.error(e.message, e.subtext);
        this.logger.error(`CreateQuickReport`, JSON.stringify(e));
        // throw new Error(e as any);
      });
  }

  @Action(CheckInventoryExistence)
  checkInventoryExistence(sc: StateContext<ReportStateModel>, {payload}: CheckInventoryExistence) {
    const quickInfoData = payload.quickInfoData;
    const quickReportKey = `${quickInfoData.year}_${quickInfoData.make}_${quickInfoData.model}_${quickInfoData.zipCode}`;
    return this.reportService.checkInventoryExistence(quickInfoData)
      .then(async (result) => {
        const hasInventory = result.data.hasInventory;
        if (!getNested(result, 'data', 'hideMessage')) {
          this.toast.success(result.message, result.subtext);
        }
        sc.patchState({
          hasInventory: Object.assign({}, sc.getState().hasInventory, {
            [quickReportKey]: hasInventory
          })
        });
      }, (e: ServerResponse) => {
        sc.patchState({
          hasInventory: Object.assign({}, sc.getState().hasInventory, {
            [quickReportKey]: false
          })
        });
        if (!getNested(e, 'data', 'hideMessage')) {
          this.toast.error(e.message, e.subtext);
        }
      });
  }

  @Action(UpdateQuickReport)
  updateQuickReport(sc: StateContext<ReportStateModel>, {payload}: UpdateQuickReport) {
    try {
      const additionalReportMetadata = payload.additionalReportMetadata;
      return this.reportService.updateQuickReport(additionalReportMetadata)
        .then(async (result) => {
          serverResponseToast(this.toast, result, 9000);
          this.analytics.trackBusinessMetric('quick-report', null, 'edit', 'success');
        }, (e) => {
          this.toast.error(e.message);
          throw new Error(e.message);
        });
    } catch (e) {
      this.toast.error(e.message);
      this.logger.error(`CreateQuickReport`, JSON.stringify(e.stack));
    }
  }

  @Action(RefreshReport)
  refreshReport(sc: StateContext<ReportStateModel>, {payload}: RefreshReport) {
    const reportId = payload.reportId;
    const reportData = payload.reportData;
    const action = payload.action;
    try {
      return this.reportService.refreshReport(reportId, reportData, action)
        .then(async (result) => {
          this.storage.clear(EXISTING_REPORT_VEHICLE_KEY);
          this.storage.clear(EXISTING_REPORT_SEND_OPTIONS_KEY);
          this.storage.clear(EXISTING_REPORT_PRICE_KEY);
          const _reportData = {
            ...result.data.reportData,
            id: reportId
          };
          this.analytics.trackBusinessMetric('report', 'refresh-report', 'create', 'success', {
            reportId
          });

          serverResponseToast(this.toast, result);
          sc.patchState({
            reports: Object.assign({}, sc.getState().reports, {[reportId]: _reportData})
          });
          this.routingService.navigateToReport(reportId, {isGenerating: 'true', action});
        }, (e) => {
          this.toast.error(e.message);
          throw new Error(e.message);
        });
    } catch (e) {
      this.toast.error(e.message);
      this.logger.error(`RefreshReport`, JSON.stringify(e.stack));
    }
  }

  storeCreatedReportLocal(reportData: RawReportData) {
    try {
      let userReportKey;

      if (reportData.vehicle.specificationMode === 'ymmt') {
        const makeKey = reportData.vehicle.make.replace(' ', '_');
        const modelKey = reportData.vehicle.model.replace(' ', '_');
        userReportKey = `${makeKey}_${modelKey}`.toLowerCase();
      } else if (reportData.vehicle.specificationMode === 'bodyType') {
        userReportKey = reportData.vehicle.bodyType.replace(' ', '_');
      }

      // Retrieve the user's already created data if it exists otherwise create an empty object
      const reportsCreatedList = this.storage.retrieveValue(this.createdReportsDateKey) || {};

      reportsCreatedList[userReportKey] = true;
      reportsCreatedList['updatedAt'] = parseInt(moment().format('YYYYMMDDHHmmss'), 10);

      this.storage.saveValue(this.createdReportsDateKey, reportsCreatedList);
    } catch (e) {
      this.logger.error(`Could not save created report locally - ${e}`);
    }
  }

  @Action(GetFavoriteReportsId)
  _getFavoriteReportsId(sc: StateContext<ReportStateModel>) {
    const favReportsId = sc.getState().favoriteReportsId;
    if (favReportsId && Object.keys(favReportsId).length > 0) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getFavoriteReportsId()
        .then((result) => {
          sc.patchState({
            favoriteReportsId: result || {}
          });
        });
    } catch (e) {
      this.toast.error('Could not get your saved reports', 'Please refresh');
      this.logger.error(`GetFavoriteReportsId`, JSON.stringify(e.stack));
    }
  }

  @Action(SetFavoriteReport)
  _setFavoriteReport(sc: StateContext<ReportStateModel>, {payload}: SetFavoriteReport) {
    const reportId = payload.reportId;
    const action = payload.action;
    try {
      return this.reportService.setFavoriteReport(reportId, action)
        .then(() => {
          const favoriteReportsId = Object.assign({}, sc.getState().favoriteReportsId);
          if (action === 'delete') {
            delete favoriteReportsId[reportId];
          } else if (action === 'save') {
            favoriteReportsId[reportId] = true;
          }
          return sc.patchState({
            favoriteReportsId
          });
        });
    } catch (e) {
      this.toast.error('Could not save this report', 'Please try again later!');
      this.logger.error(`SetFavoriteReport`, JSON.stringify(e.stack));
    }
  }

  @Action(GetGasPrice)
  _getGasPrice(sc: StateContext<ReportStateModel>, {payload}: GetGasPrice) {
    let state = payload.state;
    const zipcode = payload.zipcode;
    const gasPrices = sc.getState().gasPrices;

    if (!state) {
      state = 'TX';
    }

    // No need to check for the presence of state data since if there's state data, zipcode is already set to true
    if (gasPrices && (zipcode && checkNested(gasPrices, zipcode))) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getGasPriceByZipCode(zipcode)
        .then((result) => {
          if (!result) {
            return this.reportService.getGasPriceByState(state)
              .then((stateResult) => {
                sc.patchState({
                  gasPrices: Object.assign({}, sc.getState().gasPrices, {[state]: stateResult, [zipcode]: -1})
                });
              });
          }
          sc.patchState({
            gasPrices: Object.assign({}, sc.getState().gasPrices, {[zipcode]: result})
          });
        })
        .catch(e => {
          return this.reportService.getGasPriceByState(state)
            .then((stateResult) => {
              sc.patchState({
                gasPrices: Object.assign({}, sc.getState().gasPrices, {[state]: stateResult, [zipcode]: -1})
              });
            });
        });
    } catch (e) {
      this.logger.error('GetGasPrice', JSON.stringify(e.stack));
    }
  }

  @Action(GetElectricityPrice)
  _getElectricityPrice(sc: StateContext<ReportStateModel>, {payload}: GetElectricityPrice) {
    let state = payload.state;
    const gasPrices = sc.getState().electricityPrices;

    if (!state) {
      state = 'TX';
    }

    if (gasPrices && checkNested(gasPrices, state)) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getElectricityPriceByState(state)
        .then((result) => {
          sc.patchState({
            electricityPrices: Object.assign({}, sc.getState().electricityPrices, {[state]: result})
          });
        });
    } catch (e) {
      this.logger.error('GetElectricityPrice', JSON.stringify(e.stack));
    }
  }

  @Action(GetPossibleReportsInventory)
  _getPossibleReportsInventory(sc: StateContext<ReportStateModel>, {payload}: GetPossibleReportsInventory) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const possibleReportId = payload.possibleReportId;
    const possibleReportsInventories = sc.getState().possibleReportsInventory;
    const key = `${reportId}_${resultId}_${possibleReportId}`;

    if (checkNested(possibleReportsInventories, key)) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getPossibleReportsInventory(reportId, resultId, possibleReportId)
        .then((result) => {
          sc.patchState({
            possibleReportsInventory: Object.assign({}, sc.getState().possibleReportsInventory, {[key]: result})
          });
        });
    } catch (e) {
      this.logger.error('GetPossibleReportsInventory', JSON.stringify(e.stack));
    }
  }

  @Action(GetListingFilters)
  _getListingFilters(sc: StateContext<ReportStateModel>, {payload}: GetListingFilters) {
    const reportId = payload.reportId;
    const resultId = payload.resultId;
    const sectionId = payload.sectionId;
    const allListingFilters = sc.getState().listingFilters;
    const key = `${reportId}_${resultId}_${sectionId}`;

    if (checkNested(allListingFilters, key)) {
      return Promise.resolve(true);
    }
    try {
      return this.reportService.getListingFilters(reportId, resultId, sectionId)
        .then((result) => {
          sc.patchState({
            listingFilters: Object.assign({}, sc.getState().listingFilters, {[key]: result})
          });
        });
    } catch (e) {
      this.logger.error('GetListingFilters', JSON.stringify(e.stack));
    }
  }

  mapDoc(document) {
    if (document.exists()) {
      return {
        id: document.id,
        ...document.data()
      };
    }
    return null;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
